<template>
  <div
    :class="embedded ? 'result-dialog-embed' : ['result-dialog-mask', { 'result-dialog-mask--sp': isSpScreen }]"
    :aria-label="dialogTitle"
    @click.self="onMaskClick"
  >
    <div
      class="result-dialog"
      :class="{ 'result-dialog--embedded': embedded }"
      :role="embedded ? undefined : 'dialog'"
      :aria-modal="embedded ? undefined : 'true'"
      @click.stop
    >
      <div v-if="!embedded" class="result-dialog__header">
        <button
          v-if="isSpScreen"
          type="button"
          class="result-dialog__back"
          :aria-label="$t('assistantCatHome.back')"
          @click="$emit('close')"
        >
          <icon-arrow-left />
        </button>
        <h2 class="result-dialog__title">{{ dialogTitle }}</h2>
        <button
          v-if="!isSpScreen"
          type="button"
          class="result-dialog__close"
          :aria-label="$t('common.close')"
          @click="$emit('close')"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      <div class="result-dialog__body">
        <assistant-cat-home-work-select-prompt
          v-if="isSpScreen"
          :message="headerMessage"
          :writer-type="assist.writerType"
          variant="page"
          plain
          class="result-dialog__message"
        />
        <chat-message
          v-else
          :message="headerMessage"
          cat-icon-size="medium"
          :writer-type="assist.writerType"
          surface
          class="result-dialog__message"
        />

        <div class="result-dialog__feedback">
          <div
            ref="feedbackHtmlRoot"
            class="result-dialog__feedback-text"
            :class="{ 'is-html': isHtml }"
          >
            <div v-if="isHtml" v-html="$sanitize(resultText)"></div>
            <div v-else>{{ resultText }}</div>
          </div>
        </div>

        <div class="result-dialog__actions">
          <button
            v-if="canApplyThemeGenerate"
            type="button"
            class="result-dialog__apply-button"
            :disabled="isApplyingThemeGenerate"
            @click="handleApplyThemeGenerate"
          >
            {{ isApplyingThemeGenerate ? $t("themeGenerate.apply.applying") : $t("themeGenerate.apply.button") }}
          </button>
          <button type="button" class="result-dialog__back-button" @click="$emit('close')">
            {{ showBackToListLabel ? $t("assistantCatHome.result.backToList") : $t("common.close") }}
          </button>
        </div>

        <div class="result-dialog__notice">
          <p class="result-dialog__notice-text">{{ $t("assistantCatHome.result.historyNote") }}</p>
          <p v-if="isMatchingThemeResult" class="result-dialog__notice-text">
            {{ $t("themeMatching.result.disclaimerRecruitment") }}
          </p>
          <p v-if="isGenerateThemeResult" class="result-dialog__notice-text">
            {{ $t("themeGenerate.apply.notice") }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import AssistantCatHomeWorkSelectPrompt from "@/components/molecules/AssistantCatHomeWorkSelectPrompt.vue";
import ChatMessage from "@/components/molecules/ChatMessage.vue";
import { assistResultText, isAssistHtmlResult } from "@/constants/assistantCatHomeChat";
import { Assist, AssistFunctionType } from "@/lib/models/assist";
import {
  bindAssistHtmlResultInteractions,
  enhanceAssistHtmlResult,
} from "@/lib/utils/assistHtmlResult";
import type { ThemeMatchingCtaClick, ThemeMatchingIssue } from "@/lib/utils/assistHtmlResult";
import { ThemeGenerateAnalytics, ThemeMatchingAnalytics } from "@/lib/utils/analytics/theme";
import { scrollToThemeField } from "@/lib/utils/themeMatchingIssues";
import {
  extractThemeGenerateFieldsFromRoot,
  stashPendingThemeGenerateApply,
} from "@/lib/utils/themeGenerateApply";
import spScreenMixin from "@/mixins/spScreenMixin";
import IconArrowLeft from "~icons/figma/ArrowLeft";

const HEADER_KEYS: Record<string, string> = {
  FEEDBACK_MANUSCRIPT: "assistantCatHome.result.header.FEEDBACK_MANUSCRIPT",
  MATCHING_COMPANY: "assistantCatHome.result.header.MATCHING_COMPANY",
  GENERATE_CATCHCOPY: "assistantCatHome.result.header.GENERATE_CATCHCOPY",
  GENERATE_SUMMARY: "assistantCatHome.result.header.GENERATE_SUMMARY",
  FEEDBACK_THEME: "assistantCatHome.result.header.FEEDBACK_THEME",
  MATCHING_THEME: "assistantCatHome.result.header.MATCHING_THEME",
  GENERATE_THEME: "assistantCatHome.result.header.GENERATE_THEME",
};

export default Vue.extend({
  name: "AssistantCatHomeResultDialog",
  mixins: [spScreenMixin],
  components: {
    AssistantCatHomeWorkSelectPrompt,
    ChatMessage,
    IconArrowLeft,
  },
  props: {
    assist: {
      type: Object as () => Assist,
      required: true,
    },
    embedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      unbindThemeMatching: null as (() => void) | null,
      themeMatchingAnalytics: null as ThemeMatchingAnalytics | null,
      themeGenerateAnalytics: null as ThemeGenerateAnalytics | null,
      hasTrackedResultView: false,
      isApplyingThemeGenerate: false,
    };
  },
  computed: {
    showBackToListLabel(): boolean {
      return this.embedded || this.isSpScreen;
    },
    dialogTitle(): string {
      return this.assist.requestTitle?.trim() || (this.$t("assistantCatHome.result.pageTitle") as string);
    },
    headerMessage(): string {
      const key = HEADER_KEYS[this.assist.functionType];
      if (key && this.$te(key)) {
        return this.$t(key) as string;
      }
      return this.$t("assistantCatHome.result.header.FEEDBACK_MANUSCRIPT") as string;
    },
    isMatchingThemeResult(): boolean {
      return this.assist.functionType === AssistFunctionType.MATCHING_THEME;
    },
    isGenerateThemeResult(): boolean {
      return this.assist.functionType === AssistFunctionType.GENERATE_THEME;
    },
    canApplyThemeGenerate(): boolean {
      return this.isGenerateThemeResult && this.isHtml;
    },
    resultText(): string {
      if (this.assist.isError) {
        if (this.isMatchingThemeResult) {
          return this.$t("themeMatching.result.error") as string;
        }
        if (this.isGenerateThemeResult) {
          return this.$t("themeGenerate.result.error") as string;
        }
        return this.$t("assistantCatHome.chat.historyFailed") as string;
      }
      if (!this.assist.completed) {
        if (this.isMatchingThemeResult) {
          return this.$t("themeMatching.result.processing") as string;
        }
        if (this.isGenerateThemeResult) {
          return this.$t("themeGenerate.result.processing") as string;
        }
        return this.$t("assistantCatHome.chat.historyThinking") as string;
      }
      const text = assistResultText(this.assist);
      if (text) {
        return text;
      }
      if (this.isMatchingThemeResult) {
        return this.$t("themeMatching.result.notFound") as string;
      }
      if (this.isGenerateThemeResult) {
        return this.$t("themeGenerate.result.notFound") as string;
      }
      return this.$t("assistantCatHome.chat.historyEmpty") as string;
    },
    isHtml(): boolean {
      if (!this.assist.completed || this.assist.isError || !isAssistHtmlResult(this.assist.functionType)) {
        return false;
      }
      return /<\/?[a-z][\s\S]*>/i.test(assistResultText(this.assist));
    },
  },
  mounted() {
    if (this.$gtm) {
      this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
      this.themeGenerateAnalytics = new ThemeGenerateAnalytics(this.$gtm);
    }
    this.$nextTick(() => {
      this.setupThemeMatchingBindings();
      this.trackThemeGenerateResultView();
    });
  },
  beforeDestroy() {
    this.clearThemeMatchingBindings();
  },
  watch: {
    resultText() {
      this.hasTrackedResultView = false;
      this.$nextTick(() => {
        this.setupThemeMatchingBindings();
        this.trackThemeGenerateResultView();
      });
    },
  },
  methods: {
    onMaskClick() {
      if (!this.embedded && !this.isSpScreen) {
        this.$emit("close");
      }
    },
    clearThemeMatchingBindings(): void {
      if (this.unbindThemeMatching) {
        this.unbindThemeMatching();
        this.unbindThemeMatching = null;
      }
    },
    getOrCreateMatchingAnalytics(): ThemeMatchingAnalytics | null {
      if (!this.themeMatchingAnalytics && this.$gtm) {
        this.themeMatchingAnalytics = new ThemeMatchingAnalytics(this.$gtm);
      }
      return this.themeMatchingAnalytics;
    },
    getOrCreateGenerateAnalytics(): ThemeGenerateAnalytics | null {
      if (!this.themeGenerateAnalytics && this.$gtm) {
        this.themeGenerateAnalytics = new ThemeGenerateAnalytics(this.$gtm);
      }
      return this.themeGenerateAnalytics;
    },
    setupThemeMatchingBindings(): void {
      this.clearThemeMatchingBindings();
      if (!this.isMatchingThemeResult || !this.isHtml) return;

      const root = this.$refs.feedbackHtmlRoot as HTMLElement | undefined;
      if (!root) return;

      enhanceAssistHtmlResult(root);

      const analytics = this.getOrCreateMatchingAnalytics();
      if (analytics && !this.hasTrackedResultView) {
        analytics.trackResultView({ novelId: this.assist.novelId, root });
        this.hasTrackedResultView = true;
      }

      this.unbindThemeMatching = bindAssistHtmlResultInteractions(root, {
        onIssueClick: (issue) => this.handleThemeMatchingIssueClick(issue),
        onBoardClick: (payload) => this.handleThemeMatchingBoardClick(payload),
      });
    },
    handleThemeMatchingBoardClick(payload: ThemeMatchingCtaClick): void {
      this.getOrCreateMatchingAnalytics()?.trackCtaClick({
        cta: "board",
        novelId: this.assist.novelId,
        companyId: payload.companyId,
      });
    },
    handleThemeMatchingIssueClick(issue: ThemeMatchingIssue): void {
      let applied = false;
      if (issue.field) {
        applied = scrollToThemeField(issue.field);
      }

      const analytics = this.getOrCreateMatchingAnalytics();
      analytics?.trackImproveView({
        novelId: this.assist.novelId,
        companyId: issue.companyId,
        field: issue.field,
      });
      analytics?.trackImproveApply({
        novelId: this.assist.novelId,
        companyId: issue.companyId,
        field: issue.field,
        applied,
      });
    },
    trackThemeGenerateResultView(): void {
      if (!this.isGenerateThemeResult || !this.isHtml || this.hasTrackedResultView) return;
      const root = this.$refs.feedbackHtmlRoot as HTMLElement | undefined;
      const fields = extractThemeGenerateFieldsFromRoot(root);
      this.getOrCreateGenerateAnalytics()?.trackResultView({
        novelId: this.assist.novelId,
        fieldCount: Object.keys(fields).length,
      });
      this.hasTrackedResultView = true;
    },
    async handleApplyThemeGenerate(): Promise<void> {
      if (!this.canApplyThemeGenerate || this.isApplyingThemeGenerate) return;
      this.isApplyingThemeGenerate = true;
      try {
        const root = this.$refs.feedbackHtmlRoot as HTMLElement | undefined;
        const fields = extractThemeGenerateFieldsFromRoot(root);
        if (Object.keys(fields).length === 0) {
          Vue.notify({
            title: this.$t("themeGenerate.apply.empty") as string,
            type: "warn",
          });
          return;
        }
        const { novelId } = this.assist;
        if (!novelId) {
          Vue.notify({
            title: this.$t("themeGenerate.apply.empty") as string,
            type: "warn",
          });
          return;
        }
        stashPendingThemeGenerateApply(novelId, fields);
        await this.$router.push({ name: "theme", params: { novelId } });
      } finally {
        this.isApplyingThemeGenerate = false;
      }
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";
@import "@/styles/assist-result-html.scss";
@import url("https://fonts.googleapis.com/css2?family=Yusei+Magic&display=swap");

.result-dialog-embed {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

.result-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background-color: var(--color-overlay);
}

.result-dialog {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  max-width: 600px;
  max-height: 85vh;
  overflow: hidden;
  background: var(--color-background-normal);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  color: var(--color-text-normal);
}

.result-dialog--embedded {
  max-width: none;
  max-height: none;
  height: 100%;
  border: none;
  border-radius: 0;
  box-shadow: none;
}

.result-dialog__header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 20px 48px 0;
}

.result-dialog__title {
  margin: 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  line-height: 1.4;
  text-align: center;
}

.result-dialog__close,
.result-dialog__back {
  position: absolute;
  top: 16px;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  color: var(--color-text-normal);
  font-size: var(--font-size-xl);
  line-height: 1;
  cursor: pointer;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.result-dialog__close {
  right: 16px;
}

.result-dialog__back {
  left: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.result-dialog__body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 16px 12px 24px;
  background-color: var(--color-background-normal);
}

.result-dialog__message {
  flex-shrink: 0;
  margin-bottom: 0;
}

.result-dialog__feedback {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
  padding: 16px;
  background-color: var(--color-surface-base);
  border-radius: var(--radius-sm);
}

.result-dialog__feedback-text {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 12px 4px 0;
  color: var(--color-text-normal);
  font-size: var(--font-size-xs);
  line-height: 1.6;
  white-space: pre-line;

  &.is-html {
    white-space: normal;
    @include assist-html-result;
  }

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.05);
    border-radius: var(--radius-xs);
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: var(--radius-xs);
  }

  @include assist-catchcopy-result;
}

.result-dialog__actions {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  justify-content: center;
  padding-top: 8px;
}

.result-dialog__apply-button {
  position: relative;
  min-width: 200px;
  max-width: 240px;
  padding: 16px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:disabled {
    background-color: var(--color-surface-base);
    color: var(--color-inactive);
    cursor: not-allowed;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.result-dialog__back-button {
  position: relative;
  padding: 16px 24px;
  border: none;
  border-radius: var(--radius-rounded);
  background-color: var(--color-primary);
  color: var(--color-text-on-fixed-bg);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;

  @include hover-overlay;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px var(--color-focus-ring);
  }
}

.result-dialog__notice {
  flex-shrink: 0;
  padding-top: 8px;
  text-align: center;
}

.result-dialog__notice-text {
  margin: 0;
  color: var(--color-text-low);
  font-size: var(--font-size-xs);
  text-align: center;
}

@include mq("sp") {
  .result-dialog-mask {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
    background-color: var(--color-background-normal);
  }

  .result-dialog {
    width: 100%;
    max-width: none;
    max-height: none;
    height: 100%;
    border: none;
    border-radius: 0;
    box-shadow: none;
    background: var(--color-background-normal);
  }

  .result-dialog__header {
    height: calc(44px + env(safe-area-inset-top, 0px));
    padding: env(safe-area-inset-top, 0px) 48px 0;
    background: var(--color-background-invert);
    color: var(--color-text-invert);
  }

  .result-dialog__title,
  .result-dialog__back {
    color: var(--color-text-invert);
  }

  .result-dialog__title {
    font-size: var(--font-size-md);
  }

  .result-dialog__close {
    display: none;
  }

  .result-dialog__back {
    top: calc(env(safe-area-inset-top, 0px) + 6px);

    ::v-deep .figma-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
    }
  }

  .result-dialog__body {
    gap: 0;
    padding: 0 0 24px;
    background: var(--color-background-normal);
  }

  .result-dialog__feedback {
    margin: 0 16px;
    padding: 16px;
    border-radius: var(--radius-sm);
  }

  .result-dialog__actions {
    padding: 24px 64px 0;
  }

  .result-dialog__apply-button,
  .result-dialog__back-button {
    width: 100%;
    max-width: none;
    padding: 16px 24px;
    font-size: var(--font-size-sm);
  }

  .result-dialog__back-button {
    background-color: var(--color-text-low);
  }

  .result-dialog__notice {
    padding: 16px 16px 0;
  }
}
</style>
